import { useEffect, useRef, useState } from "react";
import { useAnnouncementPreview } from "../api/hooks";
import { MutationError, btnSecondary } from "./ui";

/**
 * Play / pause / stop for an announcement (the owner's ask of 2026-09-18,
 * and again after the first listen: controls, not a "preview" button).
 * Play asks Polly for the audio the first time - and again whenever the
 * text or the voice has changed since - then plays it. The bytes come
 * back as base64 and are played from an object URL; a failure to make
 * the audio is said on screen through the shared MutationError, a
 * failure to play it on the line beside the controls - never swallowed.
 */
export function Speak({ environmentId, text, ssml, voice, label }: { environmentId: string; text: string; ssml: boolean; voice: string; label: string }) {
  const preview = useAnnouncementPreview(environmentId);
  const audio = useRef<HTMLAudioElement | null>(null);
  const made = useRef<{ key: string; url: string } | null>(null);
  const [state, setState] = useState<"stopped" | "playing" | "paused">("stopped");
  const [problem, setProblem] = useState<string>();
  const key = `${voice}\u0000${ssml ? "ssml" : "text"}\u0000${text}`;
  const fresh = made.current?.key === key;

  useEffect(() => {
    const el = new Audio();
    el.addEventListener("ended", () => setState("stopped"));
    el.addEventListener("error", () => setProblem("The browser could not play the audio."));
    audio.current = el;
    return () => {
      if (!el.paused) el.pause();
      if (made.current) URL.revokeObjectURL(made.current.url);
      made.current = null;
    };
  }, []);

  useEffect(() => {
    // Different words or a different voice: what was made no longer matches, so a paused playback does not resume into it.
    if (made.current && made.current.key !== key) {
      audio.current?.pause();
      setState("stopped");
    }
  }, [key]);

  const play = async () => {
    setProblem(undefined);
    const el = audio.current;
    if (!el) return;
    if (!fresh) {
      // Polly's refusal (bad SSML, say) is the mutation's error; it is rendered below, so nothing to catch here.
      const out = await preview.mutateAsync({ text, ssml, voice }).catch(() => undefined);
      if (!out) return setState("stopped");
      if (made.current) URL.revokeObjectURL(made.current.url);
      const bytes = Uint8Array.from(atob(out.audio), (c) => c.charCodeAt(0));
      made.current = { key, url: URL.createObjectURL(new Blob([bytes], { type: out.contentType || "audio/mpeg" })) };
      el.src = made.current.url;
      el.currentTime = 0;
    }
    try {
      await el.play();
      setState("playing");
    } catch (err) {
      setState("stopped");
      setProblem((err as Error).message || "Could not play the announcement.");
    }
  };
  const pause = () => {
    audio.current?.pause();
    setState("paused");
  };
  const stop = () => {
    if (audio.current) {
      audio.current.pause();
      audio.current.currentTime = 0;
    }
    setState("stopped");
  };
  const busy = preview.isPending;
  const small = `${btnSecondary} min-h-8 px-2.5 text-xs`;

  return (
    <div className="inline-flex flex-wrap items-center gap-1.5">
      {state === "playing" ? (
        <button type="button" className={small} onClick={pause} aria-label={`Pause ${label}`}>❚❚ Pause</button>
      ) : (
        <button type="button" className={small} onClick={() => void play()} disabled={busy || !text.trim()} aria-label={`Play ${label}`}>
          {busy ? "Making…" : `▶ ${state === "paused" ? "Resume" : "Play"}`}
        </button>
      )}
      <button type="button" className={small} onClick={stop} disabled={state === "stopped"} aria-label={`Stop ${label}`}>■ Stop</button>
      {problem && <span role="alert" className="text-xs text-red-700 dark:text-red-300">{problem}</span>}
      <MutationError of={preview} className="basis-full" />
    </div>
  );
}
